import { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import { useFetchChargebackConfig, useUpdateChargebackConfig } from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { ChargebackConfig } from '@/lib/commissions/types';

interface ChargebackFormProps {
  onSaved?: () => void;
}

// Estados de contrato disponibles
const ESTADOS_CONTRATO_DISPONIBLES = [
  'Equipos por Retirar',
  'Equipos Cobrados',
  'Equipos Retirados',
];

export default function ChargebackForm({ onSaved }: ChargebackFormProps) {
  const { data: configData, isLoading } = useFetchChargebackConfig();
  const updateMutation = useUpdateChargebackConfig({ enableSuccessAlert: true });

  const [montoUnitario, setMontoUnitario] = useState<string>('8.50');
  const [estadosSeleccionados, setEstadosSeleccionados] = useState<string[]>(ESTADOS_CONTRATO_DISPONIBLES);

  // Cargar el valor actual cuando se obtiene la configuración
  useEffect(() => {
    if (configData && configData.monto_unitario !== undefined) {
      setMontoUnitario(String(configData.monto_unitario));
      // Cargar estados filtrados o usar todos por defecto
      // Si estados_contrato_filtro es undefined o null, usar todos
      // Si es un array (incluso vacío), respetar ese valor
      setEstadosSeleccionados(
        configData.estados_contrato_filtro !== undefined && configData.estados_contrato_filtro !== null
          ? configData.estados_contrato_filtro
          : ESTADOS_CONTRATO_DISPONIBLES
      );
    }
  }, [configData]);

  const handleChangeEstados = async (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    let nuevosEstados: string[];

    if (value.includes('SELECCIONAR_TODOS')) {
      nuevosEstados = estadosSeleccionados.length === ESTADOS_CONTRATO_DISPONIBLES.length
        ? []
        : ESTADOS_CONTRATO_DISPONIBLES;
    } else {
      nuevosEstados = value;
    }

    setEstadosSeleccionados(nuevosEstados);

    // Guardar automáticamente
    const config: ChargebackConfig = {
      id: configData?.id,
      monto_unitario: Number(configData?.monto_unitario || 8.50),
      activo: true,
      estados_contrato_filtro: nuevosEstados,
    };

    try {
      await updateMutation.mutateAsync(config);
      onSaved?.();
    } catch (error) {
      // Error silencioso en producción
    }
  };

  const handleSave = async () => {
    const config: ChargebackConfig = {
      id: configData?.id,
      monto_unitario: Number(montoUnitario),
      activo: true,
      estados_contrato_filtro: estadosSeleccionados,
    };

    await updateMutation.mutateAsync(config);
    onSaved?.();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Configuración de Chargeback
      </Typography>

      {/* Filtro de Estados de Contrato */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Estados de Contrato a Considerar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona qué estados de contrato se incluirán en el cálculo de chargebacks. Por defecto, se consideran todos los estados.
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="estados-contrato-label">Estados de Contrato</InputLabel>
          <Select
            labelId="estados-contrato-label"
            multiple
            value={estadosSeleccionados}
            onChange={handleChangeEstados}
            input={<OutlinedInput label="Estados de Contrato" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.length === 0 ? (
                  <Chip label="Ningún estado seleccionado" size="small" color="error" />
                ) : selected.length === ESTADOS_CONTRATO_DISPONIBLES.length ? (
                  <Chip label="Todos los estados" size="small" />
                ) : (
                  selected.map((value) => <Chip key={value} label={value} size="small" />)
                )}
              </Box>
            )}
          >
            <MenuItem value="SELECCIONAR_TODOS">
              <Checkbox checked={estadosSeleccionados.length === ESTADOS_CONTRATO_DISPONIBLES.length} />
              <ListItemText primary="Seleccionar todos" />
            </MenuItem>
            {ESTADOS_CONTRATO_DISPONIBLES.map((estado) => (
              <MenuItem key={estado} value={estado}>
                <Checkbox checked={estadosSeleccionados.indexOf(estado) > -1} />
                <ListItemText primary={estado} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {estadosSeleccionados.length === 0 && (
          <Alert severity="warning">
            ⚠️ No hay estados seleccionados. Esto significa que NO se contabilizarán chargebacks en los cálculos.
          </Alert>
        )}

        {estadosSeleccionados.length > 0 && estadosSeleccionados.length < ESTADOS_CONTRATO_DISPONIBLES.length && (
          <Alert severity="info">
            ℹ️ Solo se considerarán contratos con estado: {estadosSeleccionados.join(', ')}
          </Alert>
        )}

        {updateMutation.isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Guardando...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Formulario de configuración */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Valor Unitario de Chargeback
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define el monto fijo que se restará por cada contrato cancelado, independientemente del plan contratado.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Monto por Contrato Cancelado"
            type="number"
            value={montoUnitario}
            onChange={(e) => setMontoUnitario(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              step: '0.01',
              min: '0',
            }}
            sx={{ width: 250 }}
            size="small"
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={updateMutation.isPending || !montoUnitario || Number(montoUnitario) < 0}
          >
            Guardar
          </Button>
        </Box>

        {configData && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Configuración actual:</strong> ${Number(configData.monto_unitario).toFixed(2)} por contrato cancelado
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Información sobre cómo funciona */}
      <Alert severity="info" icon={<InfoIcon />}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
          Cálculo Automático de Chargebacks
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Los chargebacks se calculan automáticamente basándose en los contratos cancelados durante el período de comisión.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Cómo funciona:</strong>
        </Typography>
        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
          <li>
            <Typography variant="body2">
              Se consultan los contratos cancelados por cada asesor en el período de cálculo (día 26 al 25 del mes siguiente)
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Se multiplica el número de contratos cancelados por el valor unitario configurado (${Number(montoUnitario).toFixed(2)})
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Este total se resta automáticamente de la comisión del asesor
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Los detalles de los contratos cancelados se pueden ver expandiendo la fila del asesor en el reporte
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
          💡 Puedes descargar un reporte detallado de chargebacks usando el botón "Reporte Chargebacks" en la pestaña de Asesor.
        </Typography>
      </Alert>
    </Box>
  );
}
